import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Footer from "../components/Footer";
import SplitPanel from "../components/SplitPanel";
import { CAMERA_NEUTRAL_GRADIENT } from "../utils/lightingGradients";
import { setPageMeta, breadcrumbLd } from "../utils/meta";



export default function Privacy() {
  useEffect(() => {
    setPageMeta({
      title: "Privacy & Cookies",
      description: "This site collects no personal data, uses no tracking cookies, and sells nothing to third parties. Read exactly what is and isn't stored.",
      path: "/privacy/",
      jsonLdId: "privacy-jsonld",
      jsonLd: {
        "@context": "https://schema.org",
        "@graph": [
          { "@type": "WebPage", name: "Privacy & Cookies" },
          breadcrumbLd([{ name: "Home", path: "/" }, { name: "Privacy & Cookies", path: "/privacy/" }]),
        ],
      },
    });
  }, []);

  return (
    <>
    <SplitPanel gradient={CAMERA_NEUTRAL_GRADIENT} imageText={{ eyebrow: "Filtspot", title: "Privacy & Cookies", sub: "What is and isn't stored" }}>
    <main className="static-page split-article">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Home</Link> <span aria-hidden="true">/</span> <span>Privacy &amp; Cookies</span>
      </nav>

      <h1>Privacy &amp; Cookies</h1>
      <p className="static-updated">Last updated: August 2026</p>

      <p className="static-disclaimer">
        This page is provided for transparency about how this site actually works. It is not legal
        advice, and it isn't a substitute for a compliance-reviewed policy if this site is operated
        commercially at scale, consult a qualified professional for that.
      </p>

      <h2>Privacy policy</h2>
      <p>
        This site does not collect, store, or sell any personal data. There are no user accounts, no
        analytics trackers, no advertising pixels, no third-party embeds, and no data shared or sold
        to anyone.
      </p>
      <p>
        The gated on-device analysis is unlocked with an access code that is checked entirely in your
        browser. Nothing about that check, the code you enter, whether it succeeded, or which page
        you unlocked, is sent to a server. It isn't an account system and doesn't identify you.
      </p>
      <p>
        Like virtually any website, the hosting/CDN provider serving these pages may keep standard
        technical server logs (e.g. IP address and request timestamp) for security and operational
        purposes. This site's own code has no part in that; it's simply how web hosting works
        generally, not anything built into this application.
      </p>

      <h2>Camera and motion sensors</h2>
      <p>
        The on-device analysis on a scanned settings page uses your phone's camera and orientation
        sensor. Both are requested explicitly, and neither runs until you tap the button that starts
        it.
      </p>
      <p>
        Everything that happens next stays on your device. Camera frames are sampled into a
        32-by-32-pixel canvas in your browser to compute an average brightness figure, then
        discarded. No image is saved, transmitted, or sent to any server. There is no server in
        this site's architecture to send one to. The same is true of the orientation readings used
        to measure pan speed.
      </p>
      <p>
        The camera is released as soon as you leave the page. If your phone's camera indicator stays
        lit after that, something is wrong and we'd want to know.
      </p>

      <h2>Affiliate links</h2>
      <p>
        Some pages link to filters and memory cards at online retailers, and those links carry a
        tracking tag. If you buy something after following one, this site earns a commission and you
        pay no more than you otherwise would.
      </p>
      <p>
        Two things worth being specific about. The recommendation is not influenced by the
        commission, the filter shown is the one the exposure calculation points to, and it appears
        only on pages where the maths actually calls for one. And these are plain links: no affiliate
        network script, pixel or tracker runs on this site. Nothing is requested from a retailer
        until you click, at which point their own privacy terms apply.
      </p>

      <h2>Links to the companion app</h2>
      <p>
        Some pages link to a separate app on the Apple App Store and Google Play. These are ordinary links: following one takes you to Apple or Google, whose own privacy terms then apply. This
        site embeds nothing from either company, so no request is made to them unless you click.
      </p>
      <p>
        Which store is shown first is decided in your browser by reading the user-agent string your
        browser already sends with every request. Nothing about that check is recorded or sent
        anywhere.
      </p>

      <h2>Cookies</h2>
      <p>
        This site does not use cookies at all: not for tracking, not for advertising, not for analytics.
      </p>
      <p>
        It uses one piece of browser <strong>session storage</strong> (a different, non-cookie browser
        feature) to remember, for the current browser tab only, whether you've already entered the
        access code for a specific gated page. That value:
      </p>
      <ul>
        <li>stays on your device and is never transmitted anywhere,</li>
        <li>is cleared automatically when you close the browser tab,</li>
        <li>doesn't identify you or track you across sites or visits.</li>
      </ul>
      <p>
        Because this is strictly functional (remembering an unlock you just performed, not tracking
        or advertising), it doesn't require a cookie-consent banner under most current frameworks, 
        but again, confirm that against your own compliance obligations if this matters for your
        situation.
      </p>

      <p>
        See <Link to="/about">About</Link> for how the settings recommendations themselves are
        generated.
      </p>

    </main>
    </SplitPanel>
    <Footer />
    </>
  );
}
